import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import {
  Briefcase,
  Building2,
  Clock,
  Construction,
  FileText,
  Lock,
  Settings2,
  Tag,
  Users,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { ConfirmationModal } from "@/components/ConfirmationModal";
import { UnauthorizedPage } from "@/components/error-page";
import { hidePermsForPage, permsForPage } from "@/constants/nav-permissions";
import { EditDialog, type FieldDef } from "@/components/resource-page";
import { PolicyLevels } from "@/components/policy-levels";
import { SupplementaryLimits } from "@/components/supplementary-limits";
import { NavTabs } from "@/components/ui/nav-tabs";
import { useRecordParam } from "@/hooks/use-record-param";
import { PromoCodesSection } from "@/components/promo-codes";
import {
  COMPLIMENTARY_GROUPS,
  ENDORSEMENT_FOLLOWUP_GROUPS,
  ENDORSEMENT_GROUP,
  ReasonGroup,
  ReasonsSection,
} from "@/components/reasons";
import { CITY_FIELDS } from "@/pages/CitiesPage";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Modal } from "@/components/ui/dialog";
import { AddButton } from "@/components/ui/list-section";
import { Loading } from "@/components/ui/loading";
import { Page, PageHeader } from "@/components/ui/page";
import { SkeletonLines } from "@/components/ui/skeleton";
import { errorMessage } from "@/services/api";
import type { TranslationKey } from "@/constants/i18n";
import { deleteResource, listResource } from "@/utils/geo";
import { useCan, useHiddenFrom } from "@/utils/permissions";
import { useTranslation } from "@/store/locale";
import { cn } from "@/utils/utils";
import { useTypedSearch } from "@/hooks/use-typed-search";

/**
 * Configurations: a left rail of sections, each rendered in place. The
 * section and tab live in the URL so a page can be linked to and reloaded.
 */

const SECTIONS = [
  { key: "notes", label: "cfg.notes", icon: FileText },
  { key: "reasons", label: "cfg.reasons", icon: Settings2 },
  { key: "idle-time", label: "cfg.idleTitle", icon: Clock },
  { key: "password-expiry", label: "cfg.passwordExpiry", icon: Lock },
  { key: "promo-codes", label: "cfg.promoCodes", icon: Tag },
  { key: "business-type", label: "cfg.businessType", icon: Briefcase },
  { key: "business-unit", label: "cfg.businessUnit", icon: Building2 },
  { key: "supplementary", label: "cfg.supplementary", icon: Users },
] as const;

type SectionKey = (typeof SECTIONS)[number]["key"];

const NOTE_TABS = [
  { key: "pre-opening", label: "cfg.preOpening" },
  { key: "complimentary", label: "cfg.complimentary" },
  { key: "endorsement", label: "cfg.endorsement" },
] as const;

type NoteTab = (typeof NOTE_TABS)[number]["key"];

const searchSchema = z.object({
  section: z.enum(SECTIONS.map((s) => s.key) as [SectionKey, ...SectionKey[]]).catch("notes"),
  tab: z.enum(NOTE_TABS.map((t) => t.key) as [NoteTab, ...NoteTab[]]).catch("pre-opening"),
  // Which list has a record open, as `<list>.<token>`. Several lists share a
  // tab, so the parameter names the one it belongs to.
  edit: z.string().optional(),
});

/**
 * Both questions about one section: may this person read it, and has anybody
 * said it does not belong in their interface.
 *
 * The second is the new one, and it is off unless ticked. A role holding
 * `view__promo_code` because promo codes fill a picker on the enrolment form,
 * and `hide__promo_code` beside it, reads the data everywhere it is needed and
 * never sees the section here.
 */
const opensPage = (
  allow: (...codenames: string[]) => boolean,
  ticked: (...codenames: string[]) => boolean,
  key: string,
) => {
  const perms = permsForPage(key);
  const hidden = hidePermsForPage(key);
  // `ticked` rather than `allow` for the second half: a superuser passes every
  // `allow`, and asking that question about `hide__*` would hide every section
  // from the one person meant to see all of them.
  return (perms.length === 0 || allow(...perms)) && !(hidden.length > 0 && ticked(...hidden));
};

function ConfigurationPage() {
  const t = useTranslation();
  const allow = useCan();
  const { section, tab } = useTypedSearch(searchSchema);

  // The rail lists what this person may read. Hiding a section is a courtesy —
  // every list inside asks the API, which refuses on its own — but a rail of
  // eight doors where seven are locked is not a menu, it is a maze.
  const ticked = useHiddenFrom();
  const opens = (key: string) => opensPage(allow, ticked, key);
  const visible = SECTIONS.filter(({ key }) => opens(key));
  // Every rail link carries a tab, and Notes is the section that has them. Aim
  // at the first tab this person may read, so the rail never points at a door
  // it has already decided to keep shut.
  const noteTab = NOTE_TABS.find(({ key }) => opens(`notes.${key}`))?.key;

  // An address can be typed, bookmarked, or followed from an old link — and the
  // section and tab both default to the first one that exists, not the first one
  // this person may read. So arriving at `/configuration` with no access to
  // Notes used to land on Unauthorized while seven sections they *could* read
  // sat in the rail beside it. Send them to the first door that opens instead;
  // refusing is for someone who may read none of it, which the route guard has
  // already turned away.
  const landing = visible[0];
  const wrongSection = !visible.some(({ key }) => key === section);
  // Notes opens on any of its three tabs, so a permitted section can still name
  // a tab that is not. `noteTab` is undefined only when no tab is readable, and
  // then Notes is not in `visible` at all.
  const wrongTab =
    section === "notes" && noteTab !== undefined && tab !== noteTab && !opens(`notes.${tab}`);

  if (landing === undefined) return <UnauthorizedPage />;
  if (wrongSection || wrongTab) {
    const key = wrongSection ? landing.key : "notes";
    const to = `/configuration?section=${key}&tab=${noteTab ?? "pre-opening"}`;
    // `replace`, so Back leaves the page rather than bouncing off the redirect.
    return <Navigate to={to} replace />;
  }

  return (
    <Page>
      {/* Below `lg` the rail is a tab strip, and a strip is no place for a page
          title, so the title keeps its usual place above it. From `lg` it moves
          into the rail — see below — and this one steps aside. */}
      <PageHeader
        className="lg:hidden"
        title={t("cfg.title")}
        description={t("cfg.subtitle")}
      />
      <div className="grid gap-8 lg:grid-cols-[232px_minmax(0,1fr)] lg:gap-12">
        {/* Two shapes of the same navigation, each the right one for the room
            it has: a sticky rail from `lg`, where a column of sections can sit
            beside the content and stay answered while a long list scrolls, and
            below that a tab strip that keeps what fits and puts the rest behind
            "More" — no sideways scrolling, and every section one click away. */}
        <NavTabs
          className="lg:hidden"
          label={t("cfg.title")}
          current={section}
          items={visible.map(({ key, label, icon }) => ({
            key,
            label: t(label),
            to: `/configuration?section=${key}&tab=${noteTab}`,
            icon,
          }))}
        />
        {/* Title and sections travel together: the rail is one column that
            sticks as a piece, so "Configurations" is still overhead however far
            a section has been scrolled. Nothing passes under it — it is a grid
            column of its own — so it needs no ground of its own either. */}
        <aside className="hidden lg:sticky lg:top-6 lg:block lg:self-start">
          <h1 className="text-title-3 text-heading hairline-b pb-4">{t("cfg.title")}</h1>
          <nav className="mt-4 grid gap-0.5" aria-label={t("cfg.title")}>
            {visible.map(({ key, label, icon: Icon }) => (
              <Link
                key={key}
                to={`/configuration?section=${key}&tab=${noteTab}`}
                aria-current={section === key ? "page" : undefined}
                className={cn(
                  "text-callout flex h-9 min-w-0 items-center gap-2.5 rounded-lg px-2.5 transition-colors duration-[var(--duration-fast)]",
                  section === key
                    ? "bg-accent-soft text-accent font-medium"
                    : "text-fg hover:bg-surface-inset [&_svg]:text-fg-subtle",
                )}
              >
                <Icon className="size-4.5 shrink-0" aria-hidden />
                <span className="truncate">{t(label)}</span>
              </Link>
            ))}
          </nav>
        </aside>

        <section className="min-w-0">
          {section === "notes" ? (
            <Notes tab={tab} />
          ) : section === "supplementary" ? (
            <SupplementaryLimits />
          ) : section === "idle-time" ? (
            <PolicyLevels field="idle_timeout_minutes" />
          ) : section === "password-expiry" ? (
            <PolicyLevels field="password_change_interval_days" />
          ) : section === "promo-codes" ? (
            <PromoCodesSection />
          ) : section === "reasons" ? (
            <ReasonsSection />
          ) : section === "business-type" ? (
            <div>
              <Heading title={t("cfg.businessType")} text={t("cfg.businessTypeText")} />
              <BusinessTypeList />
            </div>
          ) : section === "business-unit" ? (
            <div>
              <Heading title={t("cfg.businessUnit")} text={t("cfg.businessUnitText")} />
              <ClubList />
            </div>
          ) : (
            <Soon section={t(SECTIONS.find((s) => s.key === section)?.label ?? "cfg.notes")} />
          )}
        </section>
      </div>
    </Page>
  );
}

function Heading({ title, text }: { title: string; text: string }) {
  return (
    <div className="mb-6">
      <h2 className="text-title-2 text-heading">{title}</h2>
      <p className="text-fg-muted text-callout mt-1">{text}</p>
    </div>
  );
}

function Notes({ tab }: { tab: NoteTab }) {
  const t = useTranslation();
  const allow = useCan();
  const ticked = useHiddenFrom();
  // The strip lists the tabs this person may read: pre-opening is a list of
  // clubs, endorsement a list of reasons, so opening Notes does not mean all
  // three. Whether `tab` is one of them is settled before this renders — the
  // page redirects to the first readable tab — so there is no refusal to draw
  // here, and only one place decides it.
  const tabs = NOTE_TABS.filter(({ key }) => opensPage(allow, ticked, `notes.${key}`));

  return (
    <div>
      <Heading title={t("cfg.notes")} text={t("cfg.notesText")} />
      <NavTabs
        className="mb-5"
        label={t("cfg.notes")}
        current={tab}
        items={tabs.map(({ key, label }) => ({
          key,
          label: t(label),
          to: `/configuration?section=notes&tab=${key}`,
        }))}
      />
      {tab === "pre-opening" ? (
        <ClubList />
      ) : tab === "endorsement" ? (
        // The reasons, then the two lists those reasons ask for: choosing
        // "Left Membership Card" on a note asks where it was left, and "Member
        // Complaint" asks what about. All three edit in a sheet over the tab,
        // so the heading and the tab strip stay put.
        <div className="divide-line divide-y">
          <ReasonGroup group={ENDORSEMENT_GROUP} boxed className="py-6 first:pt-0 last:pb-0" />
          {ENDORSEMENT_FOLLOWUP_GROUPS.map((group) => (
            <ReasonGroup
              key={group.kind}
              group={group}
              boxed
              className="py-6 first:pt-0 last:pb-0"
            />
          ))}
        </div>
      ) : tab === "complimentary" ? (
        <div className="divide-line divide-y">
          {/* "Requested by" is the club list under another name, so it answers
              to the club's own permission rather than to the tab's. Someone
              granted Complimentary but not clubs gets the reason lists and not
              this one, instead of a list whose every request comes back 403. */}
          {allow("view__club") ? (
            <ClubList
              className="py-6 first:pt-0 last:pb-0"
              title={t("cfg.requestedBy")}
              boxed
            />
          ) : null}
          {/* Every list here edits in a sheet over the tab, so the heading, the
              tab strip and the neighbouring lists stay where they are. */}
          {COMPLIMENTARY_GROUPS.map((group) => (
            <ReasonGroup
              key={group.kind}
              group={group}
              boxed
              className="py-6 first:pt-0 last:pb-0"
            />
          ))}
        </div>
      ) : (
        <Soon section={t(NOTE_TABS.find((entry) => entry.key === tab)?.label ?? "cfg.notes")} />
      )}
    </div>
  );
}

function Soon({ section }: { section: string }) {
  const t = useTranslation();
  return (
    <Card className="mt-2 grid place-items-center px-6 py-16 text-center">
      <span className="bg-surface-inset text-fg-subtle grid size-11 place-items-center rounded-full">
        <Construction className="size-5" aria-hidden />
      </span>
      <p className="text-title-3 text-heading mt-3">{t("common.comingSoon")}</p>
      <p className="text-fg-muted text-callout mt-1.5">
        {t("common.onItsWay").replace("{section}", section)}
      </p>
    </Card>
  );
}

// --- business types -----------------------------------------------------------

interface BusinessType {
  id: number;
  name: string;
  /** The concurrency stamp the API refuses a save or a delete without. */
  updated_at?: string;
}

const BUSINESS_TYPE_FIELDS = (t: (key: TranslationKey) => string): FieldDef[] => [
  { name: "name", label: t("cfg.businessTypeField"), required: true },
];

/** The business type list as designed: a name, Delete, and a way to add one. */
function BusinessTypeList({ className }: { className?: string }) {
  const t = useTranslation();
  const allow = useCan();
  const queryClient = useQueryClient();
  // Ten at a time; the next ten when the list is scrolled to its end.
  const types = useInfiniteQuery({
    queryKey: ["/business-types", "infinite"],
    queryFn: ({ pageParam, signal }) =>
      listResource<BusinessType>(
        "/business-types",
        { page: pageParam, size: LIST_PAGE, sort: "name" },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });
  const [deleting, setDeleting] = useState<BusinessType | null>(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/business-types"] });

  const remove = useMutation({
    // The stamp comes off the row being deleted — the API refuses a write that
    // does not say which version of the record it was aimed at.
    mutationFn: (row: BusinessType) =>
      deleteResource("/business-types", row.id, row.updated_at),
    onSuccess: () => {
      toast.success(t("cfg.businessTypeDeleted"));
      void invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t("cfg.businessTypeDeleteFailed"))),
  });

  const rows = types.data?.pages.flatMap((page) => page.items) ?? [];
  const {
    editing: adding,
    setEditing: setAdding,
    loading,
  } = useRecordParam<BusinessType>("edit", "business-type", "/business-types", rows);

  // A record is read when it is opened, so between the click and the answer
  // there is nothing to fill the form with. The same sheet, empty, rather than
  // the list sitting there as though the click had missed.
  const editor = loading ? (
    <Modal
      open
      onClose={() => setAdding(null)}
      title={t("rp.editSingular").replace("{singular}", t("cfg.businessType"))}
    >
      <Loading busy subject={t("cfg.businessType")}>
        <SkeletonLines rows={3} />
      </Loading>
    </Modal>
  ) : adding !== null ? (
    <EditDialog
      singular={t("cfg.businessType")}
      endpoint="/business-types"
      fields={BUSINESS_TYPE_FIELDS(t)}
      initial={adding === "new" ? {} : { name: adding.name }}
      id={adding === "new" ? null : adding.id}
      stamp={adding === "new" ? null : (adding.updated_at ?? null)}
      onClose={() => setAdding(null)}
      onSaved={() => {
        setAdding(null);
        void invalidate();
      }}
    />
  ) : null;

  return (
    <div className={className}>
      {/* What the list is, and the one thing to do to it. No count and no
          refresh: the list below says how many there are by being it, and every
          change to it is written from this screen. */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <h3 className="text-heading text-callout font-medium">
          {t("cfg.currentBusinessTypes")}
        </h3>
        {allow("add__business_type") ? (
          <AddButton onClick={() => setAdding("new")}>{t("cfg.addBusinessType")}</AddButton>
        ) : null}
      </div>

      <ul className="divide-line mt-2 divide-y">
        {types.isPending ? (
          <li className="py-2">
            <SkeletonLines rows={6} />
          </li>
        ) : null}
        {rows.map((row) => (
          <li key={row.id} className="flex items-center justify-between gap-3 py-3">
            <span className="text-fg text-callout min-w-0 break-words">{row.name}</span>
            {allow("delete__business_type") ? (
              <button
                type="button"
                onClick={() => setDeleting(row)}
                className="text-fg-muted text-subhead hover:bg-danger-soft hover:text-danger shrink-0 rounded-lg px-2 py-1 font-medium transition-colors"
              >
                {t("common.delete")}
              </button>
            ) : null}
          </li>
        ))}
        {!types.isPending && rows.length === 0 ? (
          <li className="text-fg-muted text-callout py-6 text-center">
            {t("cfg.noBusinessTypes")}
          </li>
        ) : null}
      </ul>

      {types.hasNextPage ? (
        <div className="mt-4 flex justify-end">
          <Button
            onClick={() => void types.fetchNextPage()}
            loading={types.isFetchingNextPage}
            loadingLabel={t("common.loading")}
          >
            {t("common.loadMore")}
          </Button>
        </div>
      ) : null}

      {editor}

      <ConfirmationModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
          setDeleting(null);
        }}
        title={t("common.confirmDeleteTitle").replace("{model}", t("cfg.businessType"))}
        message={deleting ? t("common.confirmDelete").replace("{name}", deleting.name) : ""}
      />
    </div>
  );
}

// --- clubs --------------------------------------------------------------------

interface Club {
  id: number;
  club_name: string;
  club_code: string;
  /** The concurrency stamp every write of a club has to carry back. */
  updated_at: string;
  // Everything but the name and the code arrives after the branch is
  // registered, so a club can be read back without any of it.
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  city_id: number | null;
}

const LIST_PAGE = 10;

// Two across, read in rows: the branch's identity on the first, then each
// half of the address beside the coordinate it resolves to — street with
// latitude, city with longitude. Only the first row is required: the rest of
// it is paperwork that catches up with the branch later, and a club has to be
// savable in between.
const CLUB_FIELDS = (t: (key: TranslationKey) => string): FieldDef[] => [
  { name: "club_code", label: t("cfg.clubId"), required: true },
  { name: "club_name", label: t("cfg.clubName"), required: true },
  { name: "address", label: t("cfg.address") },
  {
    name: "latitude",
    label: t("cfg.latitude"),
    type: "number",
    step: "any",
    min: -90,
    max: 90,
  },
  {
    name: "city_id",
    label: t("cfg.location"),
    type: "fk",
    endpoint: "/cities/compact",
    // A club opens in a place the city list has not caught up with often
    // enough that sending someone to Cities and back — losing the half-filled
    // form on the way — was the normal way to add a branch.
    create: {
      singular: t("geo.city"),
      endpoint: "/cities",
      fields: CITY_FIELDS(t),
      permission: "add__city",
    },
  },
  {
    name: "longitude",
    label: t("cfg.longitude"),
    type: "number",
    step: "any",
    min: -180,
    max: 180,
  },
];

const toForm = (club: Club): Record<string, string> => ({
  club_name: club.club_name,
  club_code: club.club_code,
  // `String(null)` is "null", which the picker would show as a selection; an
  // empty string is what it reads as nothing chosen.
  city_id: club.city_id?.toString() ?? "",
  address: club.address ?? "",
  latitude: club.latitude?.toString() ?? "",
  longitude: club.longitude?.toString() ?? "",
});

/** The club list as designed: a name, Edit, Delete, and a way to add one. Shared by three sections. */
function ClubList({
  className,
  title,
  boxed = false,
  hidden = false,
}: {
  className?: string;
  title?: string;
  boxed?: boolean;
  /** A sibling is being edited, so this one stands aside. */
  hidden?: boolean;
}) {
  const t = useTranslation();
  const allow = useCan();
  const queryClient = useQueryClient();
  // Ten at a time; the next ten when the list is scrolled to its end.
  const clubs = useInfiniteQuery({
    queryKey: ["/clubs", "infinite"],
    queryFn: ({ pageParam, signal }) =>
      listResource<Club>(
        "/clubs",
        { page: pageParam, size: LIST_PAGE, sort: "club_name" },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });
  const [deleting, setDeleting] = useState<Club | null>(null);
  const rows = clubs.data?.pages.flatMap((page) => page.items) ?? [];
  const { editing, setEditing, loading } = useRecordParam<Club>("edit", "club", "/clubs", rows);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/clubs"] });

  const remove = useMutation({
    // The stamp comes off the row being deleted — the API refuses a write that
    // does not say which version of the record it was aimed at.
    mutationFn: (club: Club) => deleteResource("/clubs", club.id, club.updated_at),
    onSuccess: () => {
      toast.success(t("cfg.clubDeleted"));
      void invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t("cfg.clubDeleteFailed"))),
  });

  if (hidden) return null;

  return (
    <div className={className}>
      {/* What the list is, and the one thing to do to it. No count and no
          refresh: the list below says how many there are by being it. */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <h3 className="text-heading text-callout font-medium">{title ?? t("cfg.club")}</h3>
        {allow("add__club") ? (
          <AddButton onClick={() => setEditing("new")}>{t("cfg.addClub")}</AddButton>
        ) : null}
      </div>

      {/* Boxed, it is one of several lists sharing a tab and keeps a scroller
          of its own; on its own screen the page is the scroller, so the list
          runs its full length and the next ten are asked for at the end. */}
      <ul
        className={cn(
          "divide-line mt-2 divide-y",
          boxed &&
            "border-line max-h-[26rem] [scrollbar-width:thin] overflow-y-auto rounded-lg border px-4",
        )}
      >
        {clubs.isPending ? (
          <li className="py-2">
            <SkeletonLines rows={6} />
          </li>
        ) : null}
        {rows.map((club) => (
          <li key={club.id} className="flex items-center justify-between gap-3 py-3">
            {/* The name alone, as the design has it: the code and the address
                are the record's, and the record is one click away. */}
            <span className="text-fg text-callout min-w-0 truncate">{club.club_name}</span>
            <span className="text-subhead flex shrink-0 items-center gap-1">
              {allow("change__club") ? (
                <button
                  type="button"
                  onClick={() => setEditing(club)}
                  className="text-fg-muted hover:bg-surface-inset hover:text-fg rounded-lg px-2 py-1 font-medium transition-colors"
                >
                  {t("common.edit")}
                </button>
              ) : null}
              {allow("delete__club") ? (
                <button
                  type="button"
                  onClick={() => setDeleting(club)}
                  className="text-fg-muted hover:bg-danger-soft hover:text-danger rounded-lg px-2 py-1 font-medium transition-colors"
                >
                  {t("common.delete")}
                </button>
              ) : null}
            </span>
          </li>
        ))}
        {!clubs.isPending && rows.length === 0 ? (
          <li className="text-fg-muted text-callout py-6 text-center">{t("cfg.noClubs")}</li>
        ) : null}
      </ul>

      {clubs.hasNextPage ? (
        <div className="mt-4 flex justify-end">
          <Button
            onClick={() => void clubs.fetchNextPage()}
            loading={clubs.isFetchingNextPage}
            loadingLabel={t("common.loading")}
          >
            {t("common.loadMore")}
          </Button>
        </div>
      ) : null}

      {/* A record is read when it is opened, so between the click and the
          answer there is nothing to fill the form with. The sheet opens on the
          skeleton rather than after it, or the click looks like it missed. */}
      {loading ? (
        <Modal
          open
          size="lg"
          title={t("rp.editSingular").replace("{singular}", t("cfg.club"))}
          onClose={() => setEditing(null)}
        >
          <Loading busy subject={t("cfg.club")}>
            <SkeletonLines rows={3} />
          </Loading>
        </Modal>
      ) : null}

      {editing !== null ? (
        <EditDialog
          singular={t("cfg.club")}
          endpoint="/clubs"
          fields={CLUB_FIELDS(t)}
          columns={2}
          initial={editing === "new" ? {} : toForm(editing)}
          id={editing === "new" ? null : editing.id}
          stamp={editing === "new" ? null : editing.updated_at}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void invalidate();
          }}
        />
      ) : null}

      <ConfirmationModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
          setDeleting(null);
        }}
        title={t("common.confirmDeleteTitle").replace("{model}", t("cfg.club"))}
        message={
          deleting
            ? t("common.confirmDelete").replace(
                "{name}",
                `${deleting.club_name} (${deleting.club_code})`,
              )
            : ""
        }
      />
    </div>
  );
}

export default ConfigurationPage;
